'use client'

import {
  Accordion,
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { env } from 'next-runtime-env'
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import AttachmentPreviewBySlug from '@/app/investor-relations/[companySlug]/AttachmentPreviewBySlug'
import { formatDublinFullDate, parseEventDate } from '@/utils/dateHelpers'
import { getFundraisingOfferStatistics } from '@/utils/investorRelations'
import type {
  IRCompanyConfig,
  IRDocument,
  IRFundraisingConfig,
  IRThemeConfig,
  InvestorRelationsConfigContextType,
} from '@/utils/investorRelations'
import type { Attachment } from '@/utils/types'
import InvestorRelationsDocumentViewer from '@/components/InvestorRelations/InvestorRelationsDocumentViewer'

interface FundraisingCampaignSectionProps {
  config: IRFundraisingConfig
  presentationDocuments: IRDocument[]
  termDocuments: IRDocument[]
  supportingDocuments: IRDocument[]
  isPreview?: boolean
  organisationId?: string
  deepLinkedDocumentId?: string | null
  onDeepLinkedDocumentClose?: (sectionId: string) => void
  onDocumentOpen?: (sectionId: string, documentId: string) => void
  useContextFunction: () => InvestorRelationsConfigContextType
}

const NEXT_PUBLIC_ASSIDUOUS_API_URL = env('NEXT_PUBLIC_ASSIDUOUS_API_URL')

export const FundraisingCampaignSection = forwardRef<
  HTMLElement,
  FundraisingCampaignSectionProps
>(function FundraisingCampaignSection(
  {
    config,
    presentationDocuments,
    termDocuments,
    supportingDocuments,
    isPreview = false,
    organisationId,
    deepLinkedDocumentId = null,
    onDeepLinkedDocumentClose,
    onDocumentOpen,
    useContextFunction,
  },
  ref
) {
  const { config: irConfig } =
    useContextFunction() as InvestorRelationsConfigContextType
  const companyConfig = (irConfig?.companyConfig || {}) as IRCompanyConfig
  const themeConfig = (irConfig?.themeConfig || {}) as IRThemeConfig
  const primaryColor = themeConfig.primaryColor || '#064E3B'
  const textColor = themeConfig.textColor || '#1F2937'
  const fontFamily = themeConfig.fontStyle
  const companySlug = irConfig?.companySlugPublished || ''
  const [selectedAttachmentForPreview, setSelectedAttachmentForPreview] =
    useState<Attachment | null>(null)
  const attachmentPreviewer = useDisclosure()

  const firstPresentation = presentationDocuments[0] || null
  const badgeAttachmentId = config.presentation.badgeAttachmentId
  const badgeUrl =
    config.presentation.badgeUrl ||
    (badgeAttachmentId && isPreview && organisationId
      ? `/api/proxy/admin/investor-relations/${organisationId}/documents/fundraising-badge/${badgeAttachmentId}`
      : badgeAttachmentId && companySlug
        ? `${NEXT_PUBLIC_ASSIDUOUS_API_URL}/investor-relations/${companySlug}/documents/fundraising-badge/${badgeAttachmentId}`
        : null)
  const managementTeamImageAttachmentId =
    config.presentation.managementTeamImageAttachmentId
  const managementTeamImageUrl =
    config.presentation.managementTeamImageUrl ||
    (managementTeamImageAttachmentId && isPreview && organisationId
      ? `/api/proxy/admin/investor-relations/${organisationId}/documents/fundraising-management-team/${managementTeamImageAttachmentId}`
      : managementTeamImageAttachmentId && companySlug
        ? `${NEXT_PUBLIC_ASSIDUOUS_API_URL}/investor-relations/${companySlug}/documents/fundraising-management-team/${managementTeamImageAttachmentId}`
        : null)
  const offerStatistics = getFundraisingOfferStatistics(config.generalInfo)

  const openAttachment = useCallback(
    (attachment: IRDocument, section: string, updateUrl = true) => {
      setSelectedAttachmentForPreview({
        ...(attachment as unknown as Attachment),
        category: section,
      })
      attachmentPreviewer.onOpen()
      if (updateUrl) {
        onDocumentOpen?.('fundraising-campaign', attachment.attachmentId)
      }
    },
    [attachmentPreviewer, onDocumentOpen]
  )

  const deepLinkDocuments = useMemo(
    () => [
      ...presentationDocuments.map((document) => ({
        document,
        section: 'fundraising-presentation',
      })),
      ...termDocuments.map((document) => ({
        document,
        section: 'fundraising-terms',
      })),
      ...supportingDocuments.map((document) => ({
        document,
        section: 'fundraising-documents',
      })),
    ],
    [presentationDocuments, supportingDocuments, termDocuments]
  )

  useEffect(() => {
    if (!deepLinkedDocumentId || attachmentPreviewer.open) return

    const target = deepLinkDocuments.find(
      (item) => item.document.attachmentId === deepLinkedDocumentId
    )
    if (!target) return

    openAttachment(target.document, target.section, false)
  }, [
    attachmentPreviewer.open,
    deepLinkDocuments,
    deepLinkedDocumentId,
    openAttachment,
  ])

  return (
    <Box
      ref={ref}
      as="section"
      id="fundraising-campaign"
      bg="white"
      minH="calc(100vh - 50px)"
      pb={{ base: 10, md: 14 }}
      fontFamily={fontFamily}
    >
      <Box
        bg="#BE0916"
        color="white"
        py={3}
        borderY="3px dotted"
        borderColor="#D6A000"
      >
        <Container maxW="7xl" position="relative">
          <HStack
            minH="28px"
            justify={{ base: 'center', md: 'flex-start' }}
            textAlign="center"
          >
            <Text fontSize="24px" fontWeight="bold" lineHeight="1.15">
              Fundraising Now Live
            </Text>
          </HStack>
          <Text
            position={{ base: 'static', md: 'absolute' }}
            top={{ md: '50%' }}
            left={{ md: '50%' }}
            transform={{ md: 'translate(-50%, -50%)' }}
            mt={{ base: 2, md: 0 }}
            fontSize="24px"
            lineHeight="1.15"
            textAlign="center"
            whiteSpace={{ base: 'normal', md: 'nowrap' }}
          >
            {companyConfig.companyName || 'Company'} Placing and Offer for
            Subscription
          </Text>
        </Container>
      </Box>

      <Container maxW="7xl" pt={{ base: 11, md: 16 }}>
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          gap={{ base: 8, lg: 12, xl: 16 }}
          align="flex-start"
          minH={{ lg: '420px' }}
        >
          <Box flex="1.35" minW={0}>
            <Stack gap={{ base: 6, md: 8 }}>
              <Heading
                as="h2"
                fontSize={{ base: '32px', md: '36px' }}
                fontWeight="semibold"
                color={primaryColor}
                lineHeight="1.15"
                pl="32px"
              >
                {companyConfig.companyName}
              </Heading>

              <Tabs.Root defaultValue="presentation" variant="plain">
                <Flex
                  align="flex-end"
                  justify="space-between"
                  gap={4}
                  position="relative"
                >
                  <Tabs.List
                    borderBottomWidth="1px"
                    borderColor="gray.200"
                    w="fit-content"
                    maxW={{
                      base: 'calc(100% - 92px)',
                      md: 'calc(100% - 112px)',
                    }}
                  >
                    {[
                      ['presentation', 'Presentation'],
                      ['terms', 'Terms'],
                      ['documents', 'Documents'],
                      ['qa', 'Q&A'],
                    ].map(([value, label]) => (
                      <Tabs.Trigger
                        key={value}
                        value={value}
                        borderBottomWidth="2px"
                        borderBottomStyle="solid"
                        borderBottomColor="transparent"
                        borderRadius="0"
                        mb="-1px"
                        _selected={{
                          borderBottomColor: primaryColor,
                          color: primaryColor,
                          fontWeight: 'semibold',
                        }}
                        css={{
                          '&[data-selected]': {
                            borderBottomColor: primaryColor,
                            color: primaryColor,
                            fontWeight: 600,
                          },
                        }}
                        fontSize={{ base: '16px', md: '17px' }}
                        fontWeight="medium"
                        lineHeight="1.3"
                        px={{ base: 3, md: 4 }}
                        py={3}
                      >
                        {label}
                      </Tabs.Trigger>
                    ))}
                  </Tabs.List>
                  {managementTeamImageUrl && (
                    <Box
                      position="absolute"
                      right={0}
                      top="50%"
                      transform="translateY(-50%)"
                      w={{ base: '78px', md: '96px' }}
                      flexShrink={0}
                    >
                      <img
                        src={managementTeamImageUrl}
                        alt=""
                        style={{
                          width: '100%',
                          height: 'auto',
                          display: 'block',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                  )}
                </Flex>
                <Tabs.Content
                  value="presentation"
                  minH={{ base: '280px', md: '300px', lg: '320px' }}
                >
                  {firstPresentation ? (
                    <Box
                      mt={4}
                      borderWidth="1px"
                      borderColor="gray.200"
                      position="relative"
                      cursor="pointer"
                      onClick={() =>
                        openAttachment(
                          firstPresentation,
                          'fundraising-presentation'
                        )
                      }
                    >
                      <AttachmentPreviewBySlug
                        attachmentType={firstPresentation.attachmentType}
                        fileName={firstPresentation.fileName}
                        fileType={firstPresentation.fileType}
                        thumbnailUrl={firstPresentation.thumbnailUrl}
                        attachmentId={firstPresentation.attachmentId}
                        companySlug={companySlug}
                        section="fundraising-presentation"
                        apiBaseUrl={NEXT_PUBLIC_ASSIDUOUS_API_URL}
                        isPreview={isPreview}
                        organisationId={organisationId || ''}
                      />
                      {badgeUrl && (
                        <Box
                          position="absolute"
                          right={4}
                          bottom={4}
                          w="120px"
                          maxW="30%"
                          borderWidth="1px"
                          borderColor="white"
                          bg="white"
                          boxShadow="md"
                        >
                          <img
                            src={badgeUrl}
                            alt=""
                            style={{ width: '100%', display: 'block' }}
                          />
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Text mt={4} color={textColor}>
                      Presentation materials will appear here when available.
                    </Text>
                  )}
                </Tabs.Content>
                <Tabs.Content
                  value="terms"
                  minH={{ base: '280px', md: '300px', lg: '320px' }}
                >
                  <DocumentList
                    documents={termDocuments}
                    section="fundraising-terms"
                    label="Terms"
                    primaryColor={primaryColor}
                    textColor={textColor}
                    fontFamily={fontFamily}
                    companySlug={companySlug}
                    apiBaseUrl={NEXT_PUBLIC_ASSIDUOUS_API_URL}
                    isPreview={isPreview}
                    organisationId={organisationId}
                    onOpen={openAttachment}
                  />
                </Tabs.Content>
                <Tabs.Content
                  value="documents"
                  minH={{ base: '280px', md: '300px', lg: '320px' }}
                >
                  <DocumentList
                    documents={supportingDocuments}
                    section="fundraising-documents"
                    label="Document"
                    primaryColor={primaryColor}
                    textColor={textColor}
                    fontFamily={fontFamily}
                    companySlug={companySlug}
                    apiBaseUrl={NEXT_PUBLIC_ASSIDUOUS_API_URL}
                    isPreview={isPreview}
                    organisationId={organisationId}
                    onOpen={openAttachment}
                  />
                </Tabs.Content>
                <Tabs.Content
                  value="qa"
                  minH={{ base: '280px', md: '300px', lg: '320px' }}
                >
                  {config.qa.length === 0 ? (
                    <Text mt={4} color={textColor}>
                      No Q&amp;A items added yet.
                    </Text>
                  ) : (
                    <Accordion.Root
                      collapsible
                      defaultValue={[config.qa[0]?.id].filter(Boolean)}
                      mt={4}
                      borderTop="1px solid"
                      borderColor="gray.200"
                    >
                      {config.qa.map((qa) => (
                        <Accordion.Item
                          key={qa.id}
                          value={qa.id}
                          border="0"
                          borderBottom="1px solid"
                          borderColor="gray.200"
                        >
                          <Accordion.ItemTrigger
                            py={4}
                            cursor="pointer"
                            _hover={{
                              '& [data-part="question-text"]': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            <Box flex="1" textAlign="left">
                              <Text
                                data-part="question-text"
                                fontSize={{ base: 'lg', md: 'xl' }}
                                fontWeight="semibold"
                                color={primaryColor}
                                fontFamily={fontFamily}
                                lineHeight="1.4"
                              >
                                {qa.question}
                              </Text>
                            </Box>
                            <Accordion.ItemIndicator color={primaryColor} />
                          </Accordion.ItemTrigger>
                          <Accordion.ItemContent>
                            <Accordion.ItemBody pt={0} pb={4}>
                              <Text
                                color={textColor}
                                fontFamily={fontFamily}
                                fontSize={{ base: 'sm', md: 'md' }}
                                lineHeight="1.75"
                                pr={{ md: 6 }}
                              >
                                {qa.answer}
                              </Text>
                            </Accordion.ItemBody>
                          </Accordion.ItemContent>
                        </Accordion.Item>
                      ))}
                    </Accordion.Root>
                  )}
                </Tabs.Content>
              </Tabs.Root>
            </Stack>
          </Box>

          <Box
            flex={{ base: '1', lg: '0 0 520px', xl: '0 0 560px' }}
            pt={{ base: 0, lg: '78px' }}
            w={{ base: '100%', lg: 'auto' }}
          >
            <Stack gap={7}>
              <Heading
                as="h3"
                fontSize={{ base: '24px', md: '32px' }}
                fontWeight="semibold"
                color={primaryColor}
                lineHeight="1.15"
                whiteSpace={{ base: 'normal', lg: 'nowrap' }}
                pl={{ base: 0, lg: 8 }}
              >
                Offer for Subscription
              </Heading>
              <Box mt={{ base: 0, lg: 5 }} pl={{ base: 0, lg: 8 }}>
                <Text
                  fontSize="17px"
                  fontWeight="bold"
                  color={primaryColor}
                  mb={5}
                >
                  Offer statistics
                </Text>
                <Stack gap={1}>
                  {offerStatistics.map((statistic) => (
                    <HStack key={statistic.id} justify="space-between" gap={8}>
                      <Text fontSize="15px" lineHeight="1.3" color={textColor}>
                        {statistic.label}
                      </Text>
                      <Text
                        fontSize="15px"
                        lineHeight="1.3"
                        color={textColor}
                        fontWeight="medium"
                      >
                        {statistic.value || '-'}
                      </Text>
                    </HStack>
                  ))}
                </Stack>
                <Stack gap={1} mt={7}>
                  {[
                    [
                      'Offer open from',
                      formatOfferDate(config.generalInfo.offerOpenFrom),
                    ],
                    [
                      'Offer closing',
                      formatOfferDate(config.generalInfo.offerClosing),
                    ],
                  ].map(([label, value]) => (
                    <HStack key={label} justify="space-between" gap={8}>
                      <Text fontSize="15px" lineHeight="1.3" color={textColor}>
                        {label}
                      </Text>
                      <Text
                        fontSize="15px"
                        lineHeight="1.3"
                        color={textColor}
                        fontWeight="medium"
                      >
                        {value || '-'}
                      </Text>
                    </HStack>
                  ))}
                </Stack>
              </Box>
              <Box pl={{ base: 0, lg: 8 }}>
                <Button
                  w="100%"
                  bg="#B98213"
                  color="white"
                  borderRadius="12px"
                  py={7}
                  fontSize={{ base: '24px', md: '28px' }}
                  fontWeight="bold"
                  _hover={{ bg: '#A87510' }}
                >
                  Register your Interest
                </Button>
              </Box>
            </Stack>
          </Box>
        </Flex>

        <Flex
          mt={{ base: 18, md: 24 }}
          direction={{ base: 'column', md: 'row' }}
          gap={{ base: 5, md: 8 }}
          align={{ base: 'stretch', md: 'flex-start' }}
        >
          <Heading
            as="h3"
            fontSize="24px"
            fontWeight="semibold"
            lineHeight="1.2"
            color={primaryColor}
            minW={{ md: '120px' }}
            pt={{ md: 2 }}
          >
            Process
          </Heading>
          <Box flex={1} position="relative">
            <Box
              display={{ base: 'none', md: 'block' }}
              position="absolute"
              top="18px"
              left="10%"
              right="10%"
              h="1px"
              bg="gray.300"
            />
            <HStack
              justify="space-between"
              align="flex-start"
              position="relative"
            >
              {[
                'Review Materials',
                'Management Meeting',
                'Identity Verification',
                'Subscription Agreement and Funds Transfer',
                'Receive Share',
              ].map((step, index) => (
                <VStack key={step} flex={1} gap={2} position="relative">
                  <Box
                    borderRadius="full"
                    bg={primaryColor}
                    color="white"
                    boxSize={9}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="16px"
                    fontWeight="semibold"
                    lineHeight="1"
                    position="relative"
                    zIndex={1}
                  >
                    {index + 1}
                  </Box>
                  <Text
                    fontSize="14px"
                    lineHeight="1.35"
                    color={textColor}
                    textAlign="center"
                    maxW="150px"
                  >
                    {step}
                  </Text>
                </VStack>
              ))}
            </HStack>
          </Box>
        </Flex>
      </Container>

      <InvestorRelationsDocumentViewer
        attachment={selectedAttachmentForPreview}
        isOpen={attachmentPreviewer.open}
        anonymousMode={true}
        companySlug={companySlug}
        section={
          selectedAttachmentForPreview?.category || 'fundraising-presentation'
        }
        isPreview={isPreview}
        organisationId={organisationId}
        companyLogoUrl={companyConfig.logoUrl}
        onClose={() => {
          const closedAttachmentId = selectedAttachmentForPreview?.attachmentId
          attachmentPreviewer.onClose()
          setSelectedAttachmentForPreview(null)
          if (closedAttachmentId === deepLinkedDocumentId) {
            onDeepLinkedDocumentClose?.('fundraising-campaign')
          }
        }}
      />
    </Box>
  )
})

function DocumentList({
  documents,
  section,
  label,
  primaryColor,
  textColor,
  fontFamily,
  companySlug,
  apiBaseUrl,
  isPreview,
  organisationId,
  onOpen,
}: {
  documents: IRDocument[]
  section: string
  label: string
  primaryColor: string
  textColor: string
  fontFamily?: string
  companySlug: string
  apiBaseUrl?: string
  isPreview?: boolean
  organisationId?: string
  onOpen: (document: IRDocument, section: string) => void
}) {
  return (
    <Box mt={5}>
      {documents.length === 0 ? (
        <Text color="gray.600">No documents added yet.</Text>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={{ base: 6, md: 7 }}>
          {documents.map((document) => (
            <VStack
              key={document.attachmentId}
              align="stretch"
              gap={3}
              cursor="pointer"
              onClick={() => onOpen(document, section)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onOpen(document, section)
                }
              }}
            >
              <Box
                borderWidth="1px"
                borderColor={primaryColor}
                borderRadius="md"
                bg="white"
                overflow="hidden"
                boxShadow="sm"
                transition="all 0.2s"
                aspectRatio="16 / 9"
                _hover={{
                  shadow: 'md',
                  transform: 'translateY(-2px)',
                }}
              >
                <AttachmentPreviewBySlug
                  attachmentType={document.attachmentType}
                  fileName={document.fileName}
                  fileType={document.fileType}
                  thumbnailUrl={document.thumbnailUrl}
                  attachmentId={document.attachmentId}
                  companySlug={companySlug}
                  section={section}
                  apiBaseUrl={apiBaseUrl}
                  isPreview={isPreview}
                  organisationId={organisationId || ''}
                />
              </Box>
              <Box>
                <Text
                  color={textColor}
                  fontSize={{ base: '16px', md: '17px' }}
                  fontWeight="medium"
                  fontFamily={fontFamily}
                  lineHeight="1.2"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {document.fileName}
                </Text>
                <HStack gap={3} mt={2} flexWrap="wrap">
                  <Text color="gray.600" fontSize="sm" fontFamily={fontFamily}>
                    {formatCampaignDocumentDate(document.publishedDate)}
                  </Text>
                  {(document.labels?.length ? document.labels : [label]).map(
                    (documentLabel) => (
                      <Box
                        key={documentLabel}
                        px={2}
                        py={1}
                        borderRadius="md"
                        bg={`${primaryColor}1A`}
                        color={primaryColor}
                        fontSize="xs"
                        fontWeight="medium"
                      >
                        {documentLabel}
                      </Box>
                    )
                  )}
                </HStack>
              </Box>
            </VStack>
          ))}
        </SimpleGrid>
      )}
    </Box>
  )
}

function formatOfferDate(value: string | null | undefined): string {
  const date = parseEventDate(value)
  return date && !Number.isNaN(date.getTime())
    ? formatDublinFullDate(date)
    : value || '-'
}

function formatCampaignDocumentDate(
  dateInput: string | null | undefined
): string {
  if (!dateInput || dateInput === '[Not published]') {
    return 'Not published'
  }

  const date = new Date(dateInput)
  if (Number.isNaN(date.getTime())) {
    return 'Not published'
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}
